import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { AlertTriangle, Trash2, CheckCircle, XCircle } from 'lucide-react';
import type { FiscalYear } from '../../types/fiscal-year';
import { fiscalYearsApi } from '../../services/api/endpoints/fiscalYears';

interface DeleteFiscalYearModalProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear: FiscalYear;
  isSuperAdmin: boolean;
  onSuccess: () => void;
}

export function DeleteFiscalYearModal({
  isOpen,
  onClose,
  fiscalYear,
  isSuperAdmin,
  onSuccess,
}: DeleteFiscalYearModalProps) {
  const [confirmCode, setConfirmCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    deleted?: {
      journals: number;
      journalLines: number;
      agentEntries: number;
      accounts: number;
    };
  } | null>(null);

  const isAudited = fiscalYear.status === 'audited';
  const canDelete = !isAudited || isSuperAdmin;
  const isConfirmed = confirmCode === fiscalYear.code;

  const handleClose = () => {
    setConfirmCode('');
    setResult(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!isConfirmed || !canDelete || loading) return;

    setLoading(true);
    try {
      const response = await fiscalYearsApi.deleteFiscalYear(fiscalYear.id, isAudited);
      if (response.success) {
        const deleted = (response as any).data?.deleted ?? (response as any).data?.data?.deleted;
        setResult({
          success: true,
          message: (response as any).message || 'Exercice fiscal supprimé avec succès',
          deleted,
        });
        setTimeout(() => {
          handleClose();
          onSuccess();
        }, 2500);
      } else {
        setResult({
          success: false,
          message: (response as any).error || 'Erreur lors de la suppression',
        });
      }
    } catch {
      setResult({
        success: false,
        message: "Erreur lors de la suppression de l'exercice fiscal",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Supprimer l'exercice ${fiscalYear.code}`}
    >
      <div className="space-y-5">
        {/* Avertissement principal */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                Action irréversible — toutes les données seront définitivement perdues
              </p>
              <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
                <li>Tous les journaux et lignes d'écriture</li>
                <li>Toutes les pièces justificatives</li>
                <li>Toutes les saisies d'agent</li>
                <li>Tous les comptes liés à cet exercice</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Avertissement exercice audité */}
        {isAudited && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                {canDelete ? (
                  <>
                    <p className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                      Exercice audité — suppression forcée (super_admin)
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                      Cet exercice a été audité et certifié. Sa suppression définitive requiert
                      l'usage du flag <code className="font-mono bg-orange-100 dark:bg-orange-900/40 px-1 rounded">force=true</code>.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                      Permission insuffisante
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                      Seul un <strong>super_admin</strong> peut supprimer un exercice fiscal audité.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Résultat de l'opération */}
        {result && (
          <div
            className={`p-4 rounded-lg ${
              result.success
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}
          >
            <div
              className={`flex items-center gap-2 text-sm font-medium ${
                result.success
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-red-700 dark:text-red-400'
              }`}
            >
              {result.success ? (
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <span>{result.message}</span>
            </div>

            {result.success && result.deleted && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                <span>
                  Journaux supprimés :{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {result.deleted.journals}
                  </strong>
                </span>
                <span>
                  Lignes d'écriture :{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {result.deleted.journalLines}
                  </strong>
                </span>
                <span>
                  Comptes supprimés :{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {result.deleted.accounts}
                  </strong>
                </span>
                <span>
                  Saisies d'agent :{' '}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {result.deleted.agentEntries}
                  </strong>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Confirmation par saisie du code (style GitHub) */}
        {canDelete && !result?.success && (
          <div className="space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Pour confirmer la suppression, tapez le code de l'exercice{' '}
              <code className="font-mono font-semibold bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-red-600 dark:text-red-400">
                {fiscalYear.code}
              </code>{' '}
              dans le champ ci-dessous :
            </p>
            <input
              type="text"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder={fiscalYear.code}
              autoComplete="off"
              disabled={loading}
              className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            />
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end gap-3 pt-1">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          {canDelete && !result?.success && (
            <Button
              variant="danger"
              icon={Trash2}
              onClick={handleDelete}
              isLoading={loading}
              disabled={!isConfirmed || loading}
            >
              {loading ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
